import React from 'react';
import moment from 'moment';
import { PropTypes as T } from 'prop-types';
import { FormGroup, FormText, Label, Input } from 'reactstrap';

import Shipment from 'app/models/shipment';

import Base from 'app/components/model_forms/base';
import BoundInput from 'app/components/forms/input/bound_input';
import BoundSelect, { Option, OptionGroup } from 'app/components/forms/input/bound_select';
import FormRow from 'app/components/bootstrap/form_row';
import ColFormLabel from 'app/components/bootstrap/col_form_label';

export const LABEL_FORMATS = [
  new Option('PDF'),
  new Option('PNG'),
  new Option('ZPL'),
  new Option('EPL2'),
];

export const ADDRESS_VERIFICATION_LEVELS = [
  new Option('', 'Full'),
  new Option('0', 'Minimal'),
];

export const PRINT_CODES = [
  new Option('', 'None'),
  new OptionGroup('FedEx', [
    new Option('PO', 'Purchase Order Number'),
    new Option('DP', 'Department Number'),
    new Option('RMA', 'Return Merchandise Authorization'),
  ]),
  new OptionGroup('UPS', [
    new Option('AJ', 'Accounts Receivable Customer Account'),
    new Option('AT', 'Appropriation Number'),
    new Option('BM', 'Bill of Lading Number'),
    new Option('9V', 'Collect on Delivery (COD) Number'),
    new Option('ON', 'Dealer Order Number'),
    new Option('DP', 'Department Number'),
    new Option('3Q', 'Food and Drug Administration (FDA) Product Code'),
    new Option('IK', 'Invoice Number'),
    new Option('MK', 'Manifest Key Number'),
    new Option('MJ', 'Model Number'),
    new Option('PM', 'Part Number'),
    new Option('PC', 'Production Code'),
    new Option('PO', 'Purchase Order Number'),
    new Option('RQ', 'Purchase Request Number'),
    new Option('RZ', 'Return Authorization Number'),
    new Option('SA', 'Salesperson Number'),
    new Option('SE', 'Serial Number'),
    new Option('ST', 'Store Number'),
    new Option('TN', 'Transaction Reference Number'),
    new Option('EI', 'Employer’s ID Number'),
    new Option('TJ', 'Federal Taxpayer ID No.'),
    new Option('SY', 'Social Security Number'),
  ]),
];

export const DELIVERY_CONFIRMATIONS = [
  new Option('', 'None'),
  new Option('SIGNATURE', 'Signature Required'),
  new Option('ADULT_SIGNATURE', 'Adult Signature Required'),
  new Option('INDIRECT_SIGNATURE', 'Indirect Signature (FedEx only)'),
];

export const ENDORSEMENTS = [
  new Option('', 'None'),
  new Option('ADDRESS_SERVICE_REQUESTED', 'Address service requested'),
  new Option('FORWARDING_SERVICE_REQUESTED', 'Forwarding service requested'),
  new Option('CHANGE_SERVICE_REQUESTED', 'Change service requested'),
  new Option('RETURN_SERVICE_REQUESTED', 'Return service requested'),
  new Option('LEAVE_IF_NO_RESPONSE', 'Leave if no response'),
];


export const COD_METHODS = [
  new Option('', 'N/A'),
  new Option('CASH', 'Cash'),
  new Option('CHECK', 'Check'),
  new Option('MONEY_ORDER', 'Money Order'),
];

export const INCOTERM = [
  new Option('', 'None'),
  new Option('EXW'),
  new Option('FCA'),
  new Option('CPT'),
  new Option('CIP'),
  new Option('DAT'),
  new Option('DAP'),
  new Option('DDP'),
  new Option('FAS'),
  new Option('FOB'),
  new Option('CFR'),
  new Option('CIF'),
];

export const HAZMAT_INDICATORS = [
  new Option('', 'None'),
  new Option('ORMD'),
  new Option('LIMITED_QUANTITY'),
];

export const SPECIAL_RATE_ELIGIBILITIES = [
  new Option('', 'None'),
  new Option('USPS.MEDIAMAIL', 'Media Mail'),
  new Option('USPS.LIBRARYMAIL', 'Library Mail'),
];

export default class ShipmentForm extends Base {
  static propTypes = {
    model: T.instanceOf(Shipment).isRequired,
  }

  componentDidMount() {
    const options = this.props.model.get('options') || {};

    $('#label_date_datepicker').daterangepicker({
      singleDatePicker: true,
      locale: { format: 'YYYY/MM/DD' },
      startDate: options.label_date || moment().utc().format(),
      autoApply: true,
      parentEl: '.modal-dialog .carrier-account',
      opens: 'right',
      applyClass: 'btn-primary',
      cancelClass: 'btn-clear',
    }, date => {
      $('[name="options[label_date]"]').val(moment(date).toISOString());
    });
  }

  render() {
    const { model } = this.props;

    return (
      <div>
        <FormGroup>
          <ColFormLabel>
            Reference Id
          </ColFormLabel>
          <BoundInput model={model} property="reference" type="text" />
          <FormText className="form-text-sm">A custom ID you can use to refer to your shipments in the API</FormText>
        </FormGroup>


        <h6>Label and Rate Options</h6>

        <FormGroup>
          <ColFormLabel>Label Format</ColFormLabel>
          <BoundSelect model={model} property="options.label_format" options={LABEL_FORMATS} />
        </FormGroup>

        <FormGroup>
          <ColFormLabel>
            Label Date
          </ColFormLabel>
          <Input id="label_date_datepicker" />
          <BoundInput model={model} type="hidden" property="options.label_date" />
          <FormText className="form-text-sm">To set the date which appears on the postage label</FormText>
        </FormGroup>

        <div className="shipment-options-carrier-specific shipment-options-carrier-specific-usps">
          <FormGroup>
            <ColFormLabel>Address Verification Level</ColFormLabel>
            <BoundSelect model={model} property="options.address_verification_level" options={ADDRESS_VERIFICATION_LEVELS} />
          </FormGroup>
        </div>

        <FormGroup>
          <ColFormLabel>
            Invoice Number
          </ColFormLabel>
          <BoundInput model={model} property="options.invoice_number" type="text" />
          <FormText className="form-text-sm">(to be printed on the shipping label)</FormText>
        </FormGroup>

        <FormRow>
          <FormGroup className="col-md-6">
            <ColFormLabel>
              Custom Label Message #1
            </ColFormLabel>
            <BoundInput model={model} property="options.print_custom_1" type="text" />
          </FormGroup>
          <FormGroup className="col-md-6">
            <ColFormLabel>
              Custom Label Message Type
            </ColFormLabel>
            <BoundSelect model={model} type="select" property="options.print_custom_1_code" options={PRINT_CODES} />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup className="col-md-6">
            <ColFormLabel>
              Custom Label Message #2
            </ColFormLabel>
            <BoundInput model={model} property="options.print_custom_2" type="text" />
          </FormGroup>
          <FormGroup className="col-md-6">
            <ColFormLabel>
              Custom Label Message Type
            </ColFormLabel>
            <BoundSelect model={model} type="select" property="options.print_custom_2_code" options={PRINT_CODES} />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup className="col-md-6">
            <ColFormLabel>
              Custom Label Message #3
            </ColFormLabel>
            <BoundInput model={model} property="options.print_custom_3" type="text" />
          </FormGroup>
          <FormGroup className="col-md-6">
            <ColFormLabel>
              Custom Label Message Type
            </ColFormLabel>
            <BoundSelect model={model} type="select" property="options.print_custom_3_code" options={PRINT_CODES} />
          </FormGroup>
        </FormRow>

        <hr />

        <h6>Delivery Options</h6>

        <FormGroup>
          <ColFormLabel>Delivery Confirmation</ColFormLabel>
          <BoundSelect model={model} property="options.delivery_confirmation" options={DELIVERY_CONFIRMATIONS} />
        </FormGroup>

        <FormGroup>
          <ColFormLabel>Endorsement</ColFormLabel>
          <BoundSelect model={model} property="options.endorsement" options={ENDORSEMENTS} />
        </FormGroup>

        <FormGroup check>
          <Label check>
            <BoundInput model={model} type="checkbox" property="options.saturday_delivery" />
            Saturday Delivery
            <FormText color="muted">
              (You will <b>only</b> receive rates for services eligible for Saturday delivery.)
            </FormText>
          </Label>
        </FormGroup>

        <FormRow>
          <FormGroup className="col-md-6">
            <ColFormLabel>C.O.D Amount</ColFormLabel>
            <BoundInput model={model} property="options.cod_amount" type="number" />
          </FormGroup>

          <FormGroup className="col-md-6">
            <ColFormLabel>C.O.D Method</ColFormLabel>
            <BoundSelect model={model} property="options.cod_method" options={COD_METHODS} />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <ColFormLabel>Incoterm</ColFormLabel>
          <BoundSelect model={model} property="options.incoterm" options={INCOTERM} />
        </FormGroup>

        <hr />

        <h6>Parcel Options</h6>

        <div className="shipment-options-carrier-specific shipment-options-carrier-specific-ups shipment-options-carrier-specific-fedex shipment-options-carrier-specific-canada-post">
          <FormGroup check>
            <Label check>
              <BoundInput model={model} type="checkbox" property="options.alcohol" />
              Contains Alcohol
              <FormText color="muted">(Only valid for UPS domestic; FedEx domestic; and Canada Post, with an adult signature.)</FormText>
            </Label>
          </FormGroup>
        </div>

        <FormGroup check>
          <Label check>
            <BoundInput model={model} type="checkbox" property="options.machinable" />
            Machinable
            <FormText color="muted">(Whether the parcel can be processed by the carriers equipment)</FormText>
          </Label>
        </FormGroup>

        <div className="shipment-options-carrier-specific shipment-options-carrier-specific-usps shipment-options-carrier-specific-fedex shipment-options-carrier-specific-dhl">
          <FormGroup>
            <ColFormLabel>Hazmat Dangerous Goods Indicator</ColFormLabel>
            <BoundSelect model={model} property="options.hazmat" options={HAZMAT_INDICATORS} />
          </FormGroup>
        </div>

        <FormRow>
          <FormGroup className="col-md-6">
            <FormGroup>
              <Label check className="pt-2">
                <BoundInput model={model} type="checkbox" property="options.dry_ice" />
                Contains Dry Ice
              </Label>
            </FormGroup>

            <FormGroup>
              <Label check>
                <BoundInput model={model} type="checkbox" property="options.dry_ice_medical" />
                Dry ice is for medical use{' '}
                <a
                  href="https://www.easypost.com/docs/api.html#options-object"
                  className="fa fa-info-circle"
                  rel="noopener noreferrer"
                  target="_blank"
                />
              </Label>
            </FormGroup>
          </FormGroup>

          <FormGroup className="col-md-6">
            <ColFormLabel>Dry Ice Weight (oz)</ColFormLabel>
            <BoundInput model={model} property="options.dry_ice_weight" type="number" />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <ColFormLabel>USPS special rates eligibility</ColFormLabel>
          <BoundSelect model={model} property="options.special_rates_eligibility" options={SPECIAL_RATE_ELIGIBILITIES} />
        </FormGroup>

        <hr />

        <h6>SmartPost Options</h6>

        <FormGroup>
          <ColFormLabel>
            SmartPost Hub
          </ColFormLabel>
          <BoundInput model={model} property="options.smartpost_hub" type="text" />
          <FormText className="form-text-sm">To override the ID on your account</FormText>
        </FormGroup>

        <FormGroup>
          <ColFormLabel>
            SmartPost Manifest
          </ColFormLabel>
          <BoundInput model={model} property="options.smartpost_manifest" type="text" />
          <FormText className="form-text-sm">To group packages on a manifest per trailer</FormText>
        </FormGroup>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/model_forms/shipment/index.jsx